import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Cookie, Check, X } from "lucide-react";
import { useCookieConsent } from "../lib/useCookieConsent";

/**
 * GDPR cookie banner (Italian, institutional dark theme).
 * Shown the first time a visitor arrives and after manual reopen
 * via the footer "Gestisci preferenze cookie" button.
 */
export default function CookieBanner() {
  const { status, accept, reject } = useCookieConsent();
  const open = status === "open";

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 40 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          data-testid="cookie-banner"
          className="fixed bottom-4 left-4 right-4 md:bottom-6 md:left-auto md:right-6 md:max-w-md z-[999]"
        >
          <div className="glass-strong rounded-2xl p-6 md:p-7 border border-white/10 shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-9 w-9 rounded-full bg-[#C48F56]/15 text-[#C48F56] grid place-items-center">
                <Cookie size={16} />
              </div>
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#C48F56]">
                Privacy & Cookie
              </div>
            </div>

            <p className="text-sm text-white/75 leading-relaxed">
              Utilizziamo esclusivamente cookie tecnici necessari al
              funzionamento del sito. Non sono installati cookie di
              profilazione o di terze parti per finalità di marketing.
            </p>

            <p className="mt-3 text-xs text-white/45 leading-relaxed">
              Per maggiori informazioni leggi la{" "}
              <Link
                to="/cookie-policy"
                className="underline decoration-dotted underline-offset-2 hover:text-[#C48F56]"
              >
                Cookie Policy
              </Link>{" "}
              e la{" "}
              <Link
                to="/privacy-policy"
                className="underline decoration-dotted underline-offset-2 hover:text-[#C48F56]"
              >
                Privacy Policy
              </Link>
              .
            </p>

            <div className="mt-6 flex flex-col sm:flex-row gap-2">
              <button
                type="button"
                onClick={reject}
                data-testid="cookie-banner-reject"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-white/15 px-4 py-2.5 text-xs uppercase tracking-[0.18em] font-mono text-white/80 hover:bg-white/5 transition-colors"
              >
                <X size={12} /> Solo necessari
              </button>
              <button
                type="button"
                onClick={accept}
                data-testid="cookie-banner-accept"
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-white text-black px-4 py-2.5 text-xs uppercase tracking-[0.18em] font-mono font-medium hover:bg-[#C48F56] transition-colors"
              >
                <Check size={12} /> Accetto
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
